import { useState } from "react";
import { cn } from "@/lib/utils";
import { valueProps, GOLD_CHECK_CIRCLE, PLAY_BTN, pricingUrl } from "@/lib/site-data";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import restoreDifference from "@/assets/restore/restore-difference.webp.asset.json";
import { useHairLossSwap } from "@/lib/hair-loss-copy";

const DEFAULT_VIDEO_URL = "https://d17ehmrb6zqtjv.cloudfront.net/restore_therestoredifference_v3.1+(1080p)+(1).mp4";

type ValuePropsProps = {
  hideEyebrowMobile?: boolean;
  hideHeadline?: boolean;
  imageSrc?: string;
  imageAlt?: string;
  videoUrl?: string;
};

export function ValueProps({
  hideEyebrowMobile = false,
  hideHeadline = false,
  imageSrc,
  imageAlt,
  videoUrl,
}: ValuePropsProps = {}) {
  const [open, setOpen] = useState(false);
  const swap = useHairLossSwap();
  const video = videoUrl ?? DEFAULT_VIDEO_URL;
  const img = imageSrc ?? restoreDifference.url;
  const alt = imageAlt ?? "The RESTORE Difference";
  return (
    <section className="bg-background py-10 md:py-14">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:px-8">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Play The RESTORE Difference video"
          className="group relative block w-full overflow-hidden"
        >
          <img loading="lazy" decoding="async"
            src={img}
            srcSet={
              imageSrc
                ? undefined
                : `/img/restore-difference-380.webp 380w, ${restoreDifference.url} 665w`
            }
            sizes="(max-width: 768px) 100vw, 665px"
            alt={alt}
            width={665}
            height={575}
            className="w-full h-auto transition group-hover:scale-105"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <img loading="lazy" decoding="async" src={PLAY_BTN} alt="" width={184} height={184} className="h-16 w-16 md:h-20 md:w-20" />
          </span>
        </button>
        <div>
          <h2
            className={cn("font-bold text-foreground", hideEyebrowMobile && "hidden md:block")}
            style={{ fontFamily: "Poppins, sans-serif", fontSize: "17px", lineHeight: "20px" }}
          >
            {swap("RESTORE Hair Transplants & Hair Restoration")}
          </h2>
          {!hideHeadline && (
            <p className="mt-3 text-4xl font-semibold leading-tight text-foreground md:text-5xl">
              {swap("There is No Better Way")}&nbsp;{swap("to")}{" "}
              <span className="font-extrabold">{swap("Restore Your Hair")}</span>
            </p>
          )}
          <div className="mt-8 space-y-6">
            {valueProps.map((v) => (
              <div key={v.title} className="flex gap-4">
                <img loading="lazy" decoding="async" src={GOLD_CHECK_CIRCLE} alt="" width={61} height={65} className="mt-1 h-8 w-8 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-foreground">{swap(v.title)}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{swap(v.body)}</p>
                </div>
              </div>
            ))}
          </div>
          <a
            href={pricingUrl}
            className="mt-8 inline-block bg-primary px-8 py-4 text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
          >
            {swap("Hair Transplant Pricing")}
          </a>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl border-0 bg-black p-0">
          <VisuallyHidden>
            <DialogTitle>Video player</DialogTitle>
          </VisuallyHidden>
          <div className="aspect-video w-full">
            {open && (
              <video src={video} controls autoPlay className="h-full w-full" playsInline />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}